import React, { useCallback, useState } from 'react';
import { Upload, X, File, AlertCircle } from 'lucide-react';

const ALLOWED_TYPES = {
  'application/pdf': '.pdf',
  'image/jpeg': '.jpg',
  'image/png': '.png',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx'
};

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const FileDropzone = ({ onFileSelect, disabled = false, accept = Object.keys(ALLOWED_TYPES).join(',') }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [error, setError] = useState('');

  const validateFile = (file) => {
    setError('');
    
    if (!file) {
      return { valid: false, error: 'No file selected' };
    }
    
    // Check file type
    if (!ALLOWED_TYPES[file.type]) {
      return { valid: false, error: 'File type not allowed. Please upload PDF, JPG, PNG, or DOCX.' };
    }
    
    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return { valid: false, error: 'File size exceeds 10MB limit.' };
    }
    
    return { valid: true, error: null };
  };

  const handleFile = useCallback((file) => {
    const validation = validateFile(file);
    if (!validation.valid) {
      setError(validation.error);
      return;
    }
    setError('');
    onFileSelect(file);
  }, [onFileSelect]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragOver(true);
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    if (disabled) return;
    
    const file = e.dataTransfer?.files?.[0];
    if (file) {
      handleFile(file);
    }
  }, [disabled, handleFile]);

  const handleInputChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFile(file);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  }, [handleFile]);

  return (
    <div className="space-y-2">
      <div
        className={`
          border-2 border-dashed rounded-lg p-6 text-center transition-all cursor-pointer
          ${isDragOver ? 'border-[#059669] bg-[#059669]/10' : 'border-gray-700 hover:border-gray-600'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !disabled && document.getElementById('file-input')?.click()}
        data-testid="file-dropzone"
      >
        <input
          id="file-input"
          type="file"
          accept={accept}
          onChange={handleInputChange}
          className="hidden"
          disabled={disabled}
        />
        
        <Upload className={`w-8 h-8 mx-auto mb-3 ${isDragOver ? 'text-[#059669]' : 'text-gray-500'}`} />
        
        <p className="text-gray-300 text-sm mb-1">
          {isDragOver ? 'Drop file here' : 'Drag & drop or click to upload'}
        </p>
        <p className="text-gray-500 text-xs">
          PDF, JPG, PNG, DOCX • Max 10MB
        </p>
      </div>
      
      {error && (
        <div className="flex items-center gap-2 text-red-400 text-sm">
          <AlertCircle className="w-4 h-4" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

// Progress bar component for upload progress
export const UploadProgress = ({ progress, fileName }) => (
  <div className="bg-[#111827] border border-gray-800 rounded-lg p-4">
    <div className="flex items-center gap-3 mb-2">
      <File className="w-5 h-5 text-[#059669]" />
      <span className="text-white text-sm truncate flex-1">{fileName}</span>
      <span className="text-gray-400 text-sm">{progress}%</span>
    </div>
    <div className="w-full bg-gray-800 rounded-full h-2">
      <div 
        className="bg-[#059669] h-2 rounded-full transition-all duration-300"
        style={{ width: `${progress}%` }}
      />
    </div>
  </div>
);

export default FileDropzone;
